import { useState, useEffect, useRef } from "react";
import type { SetURLSearchParams } from "react-router-dom";
import { trpc } from "@/trpc";
import type { Contact } from "@satyrsmc/shared/client";
import type { MeetingTemplate } from "@satyrsmc/shared/client";

export function useDebounceSearch(initial: string, delay = 300) {
  const [search, setSearch] = useState(initial);
  const [debouncedSearch, setDebouncedSearch] = useState(initial);
  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(search), delay);
    return () => clearTimeout(t);
  }, [search, delay]);
  return { search, setSearch, debouncedSearch };
}

/**
 * Debounces a search input and flushes it into the "q" URL param (replacing
 * history), resetting "page" back to 1 by default.
 *
 * Guards against re-firing on no-op re-renders: react-router's
 * `setSearchParams` gets a new identity every time `location.search` changes
 * (e.g. a pagination click), which would otherwise re-arm the debounce timer
 * and re-run this effect's body even though the search text never changed —
 * stripping the "page" param out from under an unrelated navigation.
 */
export function useUrlSearchSync(
  searchParams: URLSearchParams,
  setSearchParams: SetURLSearchParams,
  options: { delay?: number; resetPage?: boolean } = {},
) {
  const { delay = 300, resetPage = true } = options;
  const rawQ = searchParams.get("q") ?? "";
  const { search, setSearch, debouncedSearch } = useDebounceSearch(rawQ, delay);
  const prevDebouncedSearchRef = useRef(debouncedSearch);

  useEffect(() => {
    if (debouncedSearch === prevDebouncedSearchRef.current) return;
    prevDebouncedSearchRef.current = debouncedSearch;
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        if (debouncedSearch) next.set("q", debouncedSearch);
        else next.delete("q");
        if (resetPage) next.delete("page");
        return next;
      },
      { replace: true },
    );
  }, [debouncedSearch, setSearchParams]);

  return { search, setSearch };
}

/**
 * Tracks whether a page's filter bar has anything applied, and clears all
 * of it at once — both derived from one list of URL param keys instead of
 * two independently hand-maintained ones (a `hasFilters` boolean checking
 * each filter, and a separate array of keys a "Clear filters" button
 * deletes). A filter reads as "applied" whenever its raw param value is a
 * non-empty string; this works for both single-value params (`?sort=name`)
 * and comma-list multi-value ones (`?status=yes,no`), since an absent
 * param and an explicit empty one both parse back to "no restriction"
 * either way for every filter that currently uses this.
 *
 * Not itself a hook (calls no Hooks internally, so no "use" prefix) — it's
 * a plain derivation over the searchParams a caller already has from its
 * own useSearchParams(), safe to call directly in a component body.
 */
export function urlFilterState(
  searchParams: URLSearchParams,
  setSearchParams: SetURLSearchParams,
  keys: readonly string[],
) {
  const hasFilters = keys.some((key) => !!searchParams.get(key));
  const clearFilters = () => {
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      keys.forEach((key) => next.delete(key));
      return next;
    });
  };
  return { hasFilters, clearFilters };
}

/**
 * tRPC v11 useSuspenseQuery returns [data, queryResult]; older setups may return { data }.
 * Use this to get the procedure output from either shape.
 */
export function unwrapSuspenseData<T>(returnValue: [T, unknown] | { data?: T }): T | undefined {
  return Array.isArray(returnValue) ? returnValue[0] : (returnValue as { data?: T }).data;
}

/** Cache invalidation and optimistic updates (uses tRPC utils → TanStack Query) */
export function useInvalidateQueries() {
  const utils = trpc.useUtils();
  return {
    invalidateBudgets: () => utils.admin.budgets.list.invalidate(),
    invalidateBudget: (id: string) => utils.admin.budgets.get.invalidate({ id }),
    invalidateScenarios: () => utils.admin.scenarios.list.invalidate(),
    invalidateScenario: (id: string) => utils.admin.scenarios.get.invalidate({ id }),
    invalidateEvents: () => {
      utils.admin.events.list.invalidate();
      utils.admin.events.listTable.invalidate();
    },
    invalidateEvent: (id: string) => utils.admin.events.get.invalidate({ id }),
    invalidateMembers: () => utils.admin.members.list.invalidate(),
    invalidateMember: (id: string) => utils.admin.members.get.invalidate({ id }),
    invalidateContacts: () => utils.admin.contacts.list.invalidate(),
    invalidateContact: (id: string) => utils.admin.contacts.get.invalidate({ id }),
    setContactData: (id: string, data: Contact) => utils.admin.contacts.get.setData({ id }, data),
    invalidateMailingLists: () => utils.admin.mailingLists.list.invalidate(),
    invalidateMailingList: (id: string) => {
      utils.admin.mailingLists.get.invalidate({ id });
      utils.admin.mailingLists.preview.invalidate({ id });
      utils.admin.mailingLists.getStats.invalidate({ id });
      utils.admin.mailingLists.getIncluded.invalidate();
    },
    invalidateMailingBatches: () => utils.admin.mailingBatches.list.invalidate(),
    invalidateMailingBatch: (id: string) => utils.admin.mailingBatches.get.invalidate({ id }),
    invalidateQrCodes: () => utils.admin.qrCodes.list.invalidate(),
    invalidateQrCode: (id: string) => utils.admin.qrCodes.get.invalidate({ id }),
    invalidateMeetings: () => utils.admin.meetings.list.invalidate(),
    invalidateMeeting: (id: string) => utils.admin.meetings.get.invalidate({ id }),
    invalidateOldBusiness: () => utils.admin.meetings.listOldBusiness.invalidate(),
    invalidateMeetingTemplates: () => utils.admin.meetingTemplates.list.invalidate(),
    invalidateMeetingTemplate: (id: string) => utils.admin.meetingTemplates.get.invalidate({ id }),
    setMeetingTemplateData: (id: string, data: MeetingTemplate) =>
      utils.admin.meetingTemplates.get.setData({ id }, data),
    invalidateCommittees: () => utils.admin.committees.list.invalidate(),
    invalidateCommittee: (id: string) => utils.admin.committees.get.invalidate({ id }),
    invalidateCommitteeMeetings: (committeeId: string) =>
      utils.admin.committees.listMeetings.invalidate({ committeeId }),
    invalidateCommitteeMeeting: (committeeId: string, meetingId: string) =>
      utils.admin.committees.getMeeting.invalidate({
        committeeId,
        meetingId,
      }),
  };
}
